import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  AppBar,
  Toolbar,
  IconButton,
  Pagination,
} from '@mui/material';
import { Search, Logout } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchRoutes, setSearchParams, clearError, setCurrentPage } from '../store/slices/routesSlice';
import { logout } from '../store/slices/authSlice';
import { useNavigate } from 'react-router-dom';
import type { RootState } from '../store';
import { RouteData } from '../types/route';

const Dashboard: React.FC = () => {
  const [shipmentNumber, setShipmentNumber] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { routes, loading, error, pagination } = useAppSelector((state: RootState) => state.routes);
  const { clientId, isAuthenticated } = useAppSelector((state: RootState) => state.auth);

  // Auto-load recent routes when component mounts
  useEffect(() => {
    if (isAuthenticated && isInitialLoad) {
      dispatch(fetchRoutes({ 
        shipmentNumber: '', // Empty for initial load
        serialNumber: undefined,
        page: 0,
        size: 50,
        sortBy: 'id',
        sortDir: 'desc' // Most recent first for initial load
      }));
      setIsInitialLoad(false);
    }
  }, [isAuthenticated, isInitialLoad, dispatch]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (shipmentNumber.trim()) {
      setHasSearched(true);
      dispatch(setSearchParams({ 
        shipmentNumber: shipmentNumber.trim(), 
        serialNumber: serialNumber.trim() 
      }));
      dispatch(fetchRoutes({ 
        shipmentNumber: shipmentNumber.trim(), 
        serialNumber: serialNumber.trim() || undefined,
        page: 0,
        size: 50,
        sortBy: 'id',
        sortDir: 'asc'
      }));
    }
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    const page = value - 1; // Convert from 1-based to 0-based pagination
    dispatch(setCurrentPage(page));
    
    // Use current search parameters for pagination
    const currentShipmentNumber = hasSearched ? shipmentNumber.trim() : '';
    const currentSerialNumber = hasSearched ? (serialNumber.trim() || undefined) : undefined;
    const sortDirection = hasSearched ? 'asc' : 'desc'; // Recent first for initial load, asc for search
    
    dispatch(fetchRoutes({ 
      shipmentNumber: currentShipmentNumber, 
      serialNumber: currentSerialNumber,
      page,
      size: 50,
      sortBy: 'id',
      sortDir: sortDirection
    }));
  };

  const handleLogout = () => {
    dispatch(logout());
    setShipmentNumber('');
    setSerialNumber('');
    setHasSearched(false);
    setIsInitialLoad(true);
    navigate('/');
  };

  const getResultsInfo = () => {
    if (pagination.totalItems === 0) return 'No results';
    const start = pagination.currentPage * pagination.pageSize + 1;
    const end = Math.min((pagination.currentPage + 1) * pagination.pageSize, pagination.totalItems);
    return `Showing ${start}-${end} of ${pagination.totalItems} results`;
  };

  const getResultsTitle = () => {
    if (hasSearched) {
      return 'Search Results';
    }
    return 'Recent Routes';
  };

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            BeeSort Service - Dashboard
          </Typography>
          <Typography variant="body2" sx={{ mr: 2 }}>
            Welcome, {clientId}
          </Typography>
          <IconButton color="inherit" onClick={handleLogout}>
            <Logout />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom>
            Search Routes
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => dispatch(clearError())}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSearch} sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                required
                label="Shipment Number"
                value={shipmentNumber}
                onChange={(e) => setShipmentNumber(e.target.value)}
                disabled={loading}
                sx={{ minWidth: 200 }}
              />
              <TextField
                label="Serial Number (Optional)"
                value={serialNumber}
                onChange={(e) => setSerialNumber(e.target.value)}
                disabled={loading}
                sx={{ minWidth: 200 }}
              />
              <Button
                type="submit"
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} /> : <Search />}
                disabled={loading || !shipmentNumber.trim()}
                sx={{ height: 56 }}
              >
                Search
              </Button>
            </Box>
          </Box>
        </Paper>

        {routes.length > 0 && (
          <Paper elevation={3} sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h5">
                {getResultsTitle()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {getResultsInfo()}
              </Typography>
            </Box>
            
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Shipment Number</TableCell>
                    <TableCell>Route Code</TableCell>
                    <TableCell>Route Description</TableCell>
                    <TableCell>Serial Number</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>No Override</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {routes.map((route: RouteData, index: number) => (
                    <TableRow key={`${route.ShipmentNumber}-${route.SerialNumber}-${index}`}>
                      <TableCell>{route.ShipmentNumber}</TableCell>
                      <TableCell>{route.RouteCode}</TableCell>
                      <TableCell>{route.RouteDescription}</TableCell>
                      <TableCell>{route.SerialNumber}</TableCell>
                      <TableCell>{new Date(route.Date).toLocaleString()}</TableCell>
                      <TableCell>{route.NoOverride ? 'Yes' : 'No'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination Controls */}
            {pagination.totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={pagination.totalPages}
                  page={pagination.currentPage + 1} // Convert from 0-based to 1-based
                  onChange={handlePageChange}
                  disabled={loading}
                  color="primary"
                  size="large"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </Paper>
        )}

        {!loading && routes.length === 0 && hasSearched && (
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" color="text.secondary" align="center">
              No routes found for the specified criteria.
            </Typography>
          </Paper>
        )}

        {!loading && routes.length === 0 && !hasSearched && !isInitialLoad && (
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" color="text.secondary" align="center">
              No recent routes available. Use the search form above to find specific routes.
            </Typography>
          </Paper>
        )}
      </Container>
    </>
  );
};

export default Dashboard; 